/**
 * תזרים — "כמה נכנס וכמה יצא בכל חודש, מה צפוי ב-90 הימים הקרובים, ואיזה תקבול לא זיהיתי"
 * (spec-business §4.2).
 *
 * A: C4 נכנס/יוצא 12 חודשים (8) · תחזית 30/60/90 + פירוט (4) · B: C5 יתרת עו"ש (12) ·
 * C: טבלת תנועות עם סרגל סינון והתאמת תקבול לחשבונית (12).
 */

import { useState } from 'react';
import type { CashflowResponse, ForecastResponse, MatchCandidate, SummaryResponse, TransactionRow, TransactionsResponse } from '../../../api-types.ts';
import { fmtDate, fmtMoney, fmtShortDate, monthName, monthShort } from '../format.ts';
import { CATEGORY, SCOPE, he } from '../i18n/he.ts';
import { Icon } from '../components/Icon.tsx';
import { Card, CardHead, Chip, DeltaMoney, EmptyState, Mask, Money, Num, Row, SearchField, SelectField, Skeleton, SortableTh, Tile, toggleSort } from '../components/ui.tsx';
import { CategoryCell, InvoiceCell, MatchCell, TxListItem, VatCell } from '../components/tx.tsx';
import { CardVisual, ProviderMark } from '../../../../shared/ui/src/index.tsx';
import { BalanceLine } from '../charts/BalanceLine.tsx';
import { ColumnChart } from '../charts/ColumnChart.tsx';
import { useApp } from '../hooks/useApp.tsx';
import { cacheKey } from '../hooks/cache.ts';
import { useQuery } from '../hooks/useQuery.ts';
import { useRouter } from '../hooks/useRouter.tsx';
import { errorMessage } from '../api-select.ts';

type SortCol = 'date' | 'desc' | 'account' | 'cat' | 'amount' | 'vat';

export function CashflowScreen({ summary }: { summary: SummaryResponse | undefined }) {
  const { api, period, toast, openDialog } = useApp();
  const { route, navigate } = useRouter();
  const [sort, setSort] = useState<{ col: SortCol; dir: 'asc' | 'desc' }>({ col: 'date', dir: 'desc' });
  // `?account=` is a real deep link: `AccountId` is a legal value of `TransactionsQuery.account`,
  // and the loans screen links a row's transactions here (REDESIGN §6).
  const [account, setAccount] = useState(route.query.account ?? 'all');
  const [dir, setDir] = useState<'all' | 'in' | 'out'>('all');
  const [match, setMatch] = useState<'all' | 'matched' | 'unmatched'>((route.query.match as 'unmatched') ?? 'all');
  const [q, setQ] = useState('');

  const cashflow = useQuery<CashflowResponse>(cacheKey('cashflow', { period }), () => api.cashflow({ period, months: 13 }), { scopes: ['cashflow'] });
  const forecast = useQuery<ForecastResponse>(cacheKey('forecast'), () => api.forecast(), { scopes: ['forecast'] });
  const txQuery = { period, account: account === 'all' ? undefined : account, dir: dir === 'all' ? undefined : dir, match, q: q || undefined, sort: sort.col === 'amount' ? ('amount' as const) : ('date' as const) };
  const transactions = useQuery<TransactionsResponse>(cacheKey('cf-tx', { ...txQuery }), () => api.transactions(txQuery), { scopes: ['transactions'] });

  const asOf = summary?.asOf ?? cashflow.data?.asOf ?? '';
  const cardMask = `****${summary?.card?.account.numberLast4 ?? cashflow.data?.card?.account.numberLast4 ?? ''}`;

  const openMatchDialog = (tx: TransactionRow) => {
    api.transaction(tx.id).then(
      (detail) => {
        let choice: string | null = detail.matchCandidates[0]?.number ?? null;
        openDialog({
          title: he.cashflow.matchTitle,
          text: he.cashflow.matchBody(fmtMoney(tx.amountAgorot), fmtDate(tx.effDate), tx.merchant ?? tx.description),
          content: detail.matchCandidates.length ? (
            <MatchPicker candidates={detail.matchCandidates} asOf={asOf} onChange={(value) => (choice = value)} />
          ) : (
            <p className="note">{he.cashflow.matchEmpty}</p>
          ),
          actions: [
            {
              label: he.cashflow.matchAction,
              tone: 'primary',
              disabled: !detail.matchCandidates.length,
              onClick: () => {
                if (!choice) return;
                api.matchTransaction(tx.id, { invoiceNumber: choice }).then(
                  () => toast(he.cashflow.matchOk(choice!)),
                  (error: unknown) => toast(errorMessage(error), { error: true }),
                );
              },
            },
            { label: he.app.cancel },
          ],
        });
      },
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  const markInvoice = (tx: TransactionRow) => {
    api.updateExpense(tx.id, { hasTaxInvoice: true }).then(
      () => toast(he.expenses.markedHasInvoice),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  const setCategory = (tx: TransactionRow, category: Parameters<typeof api.updateExpense>[1]['category']) => {
    api.updateExpense(tx.id, { category }).then(
      () => toast(he.cashflow.categoryChanged),
      (error: unknown) => toast(errorMessage(error), { error: true }),
    );
  };

  const months = cashflow.data?.months ?? [];
  const fullMonths = months.filter((m) => !m.partial);
  const partialIndex = months.findIndex((m) => m.partial);
  const netLabelIndex = months.reduce((last, m, i) => (m.partial ? last : i), -1);

  return (
    <div className="grid cashflow">
      {/* ── A · C4 נכנס מול יוצא ───────────────────────────────────── */}
      <Card span={8} className="c4" labelledBy="h-c4">
        <CardHead
          titleId="h-c4"
          title={he.cashflow.title}
          sub={
            months.length
              ? he.cashflow.subClosed(fullMonths.length, monthName(months[months.length - 1]!.month))
              : ''
          }
        />
        {cashflow.loading ? (
          <Skeleton className="ch" />
        ) : months.length ? (
          <>
            <ColumnChart
              labels={months.map((m) => (m.partial ? `${monthShort(m.month, false)} (עד ${fmtShortDate(asOf, asOf)})` : monthShort(m.month)))}
              mode="diverging"
              partialIndex={partialIndex}
              height={250}
              series={[
                { key: 'in', label: he.cashflow.inLabel, series: 1, values: months.map((m) => m.inAgorot) },
                { key: 'out', label: he.cashflow.outLabel, series: 2, values: months.map((m) => m.outAgorot) },
              ]}
              line={{ label: he.cashflow.netLabel, values: months.map((m) => m.netAgorot), labelIndex: netLabelIndex >= 0 ? netLabelIndex : undefined }}
              summary={`${he.cashflow.title}: ${he.cashflow.inLabel} ${fmtMoney(cashflow.data!.totals.inAgorot)}, ${he.cashflow.outLabel} ${fmtMoney(cashflow.data!.totals.outAgorot)}, ${he.cashflow.netLabel} ${fmtMoney(cashflow.data!.totals.netAgorot, 0, true)}.`}
              tip={(index) => {
                const m = months[index]!;
                return {
                  title: monthName(m.month),
                  rows: [
                    { label: he.cashflow.inLabel, value: fmtMoney(m.inAgorot), color: 'var(--series-1)', square: true },
                    { label: he.cashflow.outLabel, value: fmtMoney(m.outAgorot), color: 'var(--series-2)', square: true },
                    { label: he.cashflow.netLabel, value: fmtMoney(m.netAgorot, 0, true), color: 'var(--text-1)' },
                    { label: he.cashflow.endBalance, value: fmtMoney(m.endBalanceAgorot) },
                  ],
                };
              }}
              table={{
                caption: he.cashflow.title,
                head: ['חודש', he.cashflow.inLabel, he.cashflow.outLabel, he.cashflow.netLabel, he.cashflow.endBalance],
                rows: months.map((m) => [monthName(m.month), fmtMoney(m.inAgorot), fmtMoney(m.outAgorot), fmtMoney(m.netAgorot, 0, true), fmtMoney(m.endBalanceAgorot)]),
              }}
            />
            <div className="note">
              {he.cashflow.fullMonths(fullMonths.length, monthShort(fullMonths[0]?.month ?? ''), monthShort(fullMonths[fullMonths.length - 1]?.month ?? ''))}:{' '}
              {he.cashflow.inLabel} <b><Money agorot={cashflow.data!.totals.inAgorot} /></b> · {he.cashflow.outLabel}{' '}
              <b><Money agorot={cashflow.data!.totals.outAgorot} /></b> · {he.cashflow.netLabel}{' '}
              <b><Money agorot={cashflow.data!.totals.netAgorot} plus /></b>
            </div>
          </>
        ) : (
          <EmptyState text={he.cashflow.emptyNoData} />
        )}
      </Card>

      {/* ── A · תחזית ──────────────────────────────────────────────── */}
      <Card span={4} className="fc" labelledBy="h-fc">
        <CardHead titleId="h-fc" title={he.cashflow.forecast} sub={he.cashflow.forecastSub} />
        {forecast.loading ? (
          <Skeleton className="ch" />
        ) : forecast.data ? (
          <>
            <div className="tiles t3" style={{ gap: '8px' }}>
              {forecast.data.horizons.map((h) => (
                <Tile
                  key={h.days}
                  tight
                  label={
                    <>
                      {he.cashflow.horizon(h.days)} <span className="note">{fmtShortDate(h.date, asOf)}</span>
                    </>
                  }
                  value={<Money agorot={h.balanceAgorot} />}
                  note={
                    <>
                      <DeltaMoney agorot={h.deltaAgorot} baseAgorot={forecast.data!.availableAgorot} /> {he.home.vsToday}
                    </>
                  }
                  tone={h.belowFloor ? 'critical' : undefined}
                />
              ))}
            </div>
            <details open>
              <summary className="kicker" style={{ cursor: 'pointer' }}>
                {he.cashflow.breakdown(fmtShortDate(asOf, asOf), fmtShortDate(forecast.data.horizons[0]?.date ?? '', asOf))}
              </summary>
              <div className="rows" style={{ fontSize: '13px' }}>
                {forecast.data.lines.map((line) => (
                  <Row
                    key={line.key}
                    label={
                      <span style={{ fontSize: '13px' }}>
                        {line.key === 'balance' ? '' : line.amountAgorot > 0 ? '+ ' : '− '}
                        {line.label} <span className="note">· {line.whenLabel}</span>
                      </span>
                    }
                    value={<Money agorot={Math.abs(line.amountAgorot)} />}
                  />
                ))}
                <Row
                  total
                  label={he.cashflow.forecastTotal(fmtShortDate(forecast.data.horizons[0]?.date ?? '', asOf))}
                  value={
                    <>
                      <Money agorot={forecast.data.horizons[0]?.balanceAgorot ?? 0} />{' '}
                      <DeltaMoney agorot={forecast.data.horizons[0]?.deltaAgorot ?? 0} baseAgorot={forecast.data.availableAgorot} />
                    </>
                  }
                />
                {forecast.data.exclusions.map((exclusion) => (
                  <Row key={exclusion.key} muted label={`${exclusion.label}${exclusion.amountAgorot ? ` ${fmtMoney(exclusion.amountAgorot)}` : ''}`} />
                ))}
              </div>
            </details>
          </>
        ) : (
          <EmptyState text={he.cashflow.emptyNoData} />
        )}
      </Card>

      {/* ── A · מחזור הכרטיס — REDESIGN §7c, the illustrated card ──── */}
      {summary?.card ? (
        <Card span={4} className="cc" labelledBy="h-cc">
          <CardHead titleId="h-cc" title={he.card.block} sub={he.card.blockSub(cardMask)} />
          <div className="cardcycle">
            <CardVisual
              providerId={summary.card.account.providerId}
              last4={summary.card.account.numberLast4}
              size="sm"
              state={summary.card.account.creditStatus === 'blocked' ? 'blocked' : summary.card.account.creditStatus === 'deleted' ? 'closed' : 'active'}
              stateLabel={summary.card.account.creditStatus === 'blocked' ? he.card.blocked : he.card.deleted}
              label={summary.card.account.name}
              title={`${summary.card.account.name} ${cardMask}`}
            />
            <div className="rows cardcycle__rows">
              <Row
                label={he.card.current}
                value={
                  <>
                    <Money agorot={summary.card.current.amountAgorot} />
                    {summary.card.current.pendingAgorot ? <span className="note"> {he.card.pending(fmtMoney(summary.card.current.pendingAgorot))}</span> : null}
                  </>
                }
              />
              {summary.card.next ? (
                <Row
                  label={
                    <>
                      {he.card.next} <span className="note">{he.card.due(fmtShortDate(summary.card.next.dueDate, asOf))}</span>
                    </>
                  }
                  value={
                    <>
                      <Money agorot={summary.card.next.amountAgorot} />{' '}
                      <Chip tone={summary.card.next.status === 'charged' ? 'good' : 'outline'} small>
                        {summary.card.next.status === 'charged' ? he.card.charged : he.card.final}
                      </Chip>
                    </>
                  }
                />
              ) : null}
              <Row
                label={he.card.utilization}
                value={
                  summary.card.utilization === null ? (
                    he.app.none
                  ) : (
                    <>
                      <Num>{`${Math.round(summary.card.utilization * 100)}%`}</Num>{' '}
                      <span className="note">{he.card.limit(fmtMoney(summary.card.creditLimitAgorot))}</span>
                    </>
                  )
                }
              />
            </div>
          </div>
        </Card>
      ) : null}

      {/* ── B · C5 יתרת עו"ש ───────────────────────────────────────── */}
      <Card span={12} className="c5" labelledBy="h-c5">
        <CardHead
          titleId="h-c5"
          title={he.cashflow.balanceChart}
          sub={
            forecast.data
              ? he.cashflow.balanceChartSub(
                  fmtShortDate(forecast.data.series[0]?.day ?? '', asOf),
                  fmtShortDate(forecast.data.series[forecast.data.series.length - 1]?.day ?? '', asOf),
                  fmtMoney(forecast.data.floorAgorot),
                )
              : ''
          }
        />
        {forecast.data ? (
          <BalanceLine
            points={forecast.data.series}
            events={forecast.data.events}
            asOf={asOf}
            height={230}
            floorAgorot={forecast.data.floorAgorot}
            eventKinds={['card', 'tax', 'standing_order', 'owner_draw', 'invoice']}
            labelKinds={['card', 'tax']}
            summary={`${he.cashflow.balanceChart}. ${forecast.data.horizons.map((h) => `${fmtShortDate(h.date, asOf)} ${fmtMoney(h.balanceAgorot)}`).join(', ')}.`}
            table={{
              caption: he.cashflow.balanceChart,
              head: ['תאריך', 'יתרה', 'סוג', 'אירועים'],
              rows: forecast.data.series
                .filter((p, i) => i % 5 === 0 || i === forecast.data!.series.length - 1 || forecast.data!.events.some((e) => e.day === p.day))
                .map((p) => [
                  fmtDate(p.day),
                  fmtMoney(p.balanceAgorot),
                  p.actual ? 'בפועל' : 'תחזית',
                  forecast.data!.events.filter((e) => e.day === p.day).map((e) => `${e.label} ${fmtMoney(e.amountAgorot, 0, true)}`).join(', '),
                ]),
            }}
          />
        ) : (
          <Skeleton className="ch" />
        )}
      </Card>

      {/* ── C · תנועות בנק וכרטיס ──────────────────────────────────── */}
      <Card span={12} className="tx" labelledBy="h-tx">
        <CardHead
          titleId="h-tx"
          title={he.cashflow.txTitle}
          sub={
            match === 'unmatched' ? (
              he.cashflow.txSubUnmatched
            ) : (
              <>
                {transactions.data ? monthName(transactions.data.period.lastMonth) : ''} · {he.cashflow.filterChecking} <Mask>{`***${summary?.balance.account?.numberLast4 ?? ''}`}</Mask>
                {summary?.card ? (
                  <>
                    {' + '}
                    <Mask>{cardMask}</Mask>
                  </>
                ) : null}
              </>
            )
          }
          actions={
            <button type="button" className="btn sm" onClick={() => toast(he.app.fullVersion)}>
              <Icon name="download" />
              {he.cashflow.exportCsv}
            </button>
          }
        />
        <div className="filters">
          <SelectField
            name="account"
            label={he.cashflow.filterAccount}
            value={account}
            onChange={setAccount}
            options={[
              ['all', he.cashflow.filterAccountAll],
              ['checking', he.cashflow.filterChecking],
              ['card', he.cashflow.filterCard(cardMask)],
              // an id arriving from ?account= keeps the select consistent with what is filtered
              ...(account === 'all' || account === 'checking' || account === 'card' ? [] : [[account, he.cashflow.filterAccountOne] as const]),
            ]}
          />
          <SelectField
            name="dir"
            label={he.cashflow.filterDir}
            value={dir}
            onChange={(value) => setDir(value as 'all' | 'in' | 'out')}
            options={[
              ['all', he.cashflow.filterDirAll],
              ['in', he.cashflow.filterIn],
              ['out', he.cashflow.filterOut],
            ]}
          />
          <SelectField
            name="match"
            label={he.cashflow.filterMatch}
            value={match}
            onChange={(value) => {
              setMatch(value as 'all' | 'matched' | 'unmatched');
              navigate(value === 'unmatched' ? '/cashflow?match=unmatched' : '/cashflow', { replace: true });
            }}
            options={[
              ['all', he.cashflow.filterMatchAll],
              ['matched', he.cashflow.filterMatched],
              ['unmatched', he.cashflow.filterUnmatched],
            ]}
          />
          <SearchField value={q} onChange={setQ} placeholder={he.cashflow.searchTx} label={he.cashflow.searchTxAria} />
        </div>

        {transactions.loading ? (
          <Skeleton className="ch" />
        ) : !transactions.data?.items.length ? (
          <EmptyState
            icon={match === 'unmatched' ? 'checkCircle' : 'filter'}
            good={match === 'unmatched'}
            text={match === 'unmatched' ? he.cashflow.emptyUnmatched : transactions.data?.summary.totalRows ? he.cashflow.emptyFilter : he.cashflow.emptyNoData}
            action={
              <button
                type="button"
                className="btn sm"
                onClick={() => {
                  setAccount('all');
                  setDir('all');
                  setMatch('all');
                  setQ('');
                }}
              >
                {he.app.clear}
              </button>
            }
          />
        ) : (
          <>
            <div className="tbl-wrap desk-only">
              <table className="tbl">
                <thead>
                  <tr>
                    <SortableTh label={he.cashflow.colDate} col={'date' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['date', 'amount']))} />
                    <SortableTh label={he.cashflow.colDesc} col={'desc' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['date', 'amount']))} />
                    <SortableTh label={he.cashflow.colAccount} col={'account' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['date', 'amount']))} />
                    <SortableTh label={he.cashflow.colCategory} col={'cat' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['date', 'amount']))} />
                    <SortableTh label={he.cashflow.colAmount} col={'amount' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['date', 'amount']))} numeric />
                    <SortableTh label={he.cashflow.colVat} col={'vat' as SortCol} sort={sort} onSort={(col) => setSort(toggleSort(sort, col, ['date', 'amount']))} numeric />
                    <th scope="col">{he.cashflow.colInvoice}</th>
                    <th scope="col">{he.cashflow.colMatch}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortRows(transactions.data.items, sort).map((tx) => (
                    <tr key={tx.id} className={tx.status === 'PENDING' ? 'tr-pending' : undefined}>
                      <td>
                        <Num>{fmtDate(tx.effDate)}</Num>
                      </td>
                      <td>
                        <span className="tx-name">
                          <ProviderMark providerId={tx.account.providerId} size={20} />
                          {tx.merchant ?? tx.description}
                        </span>
                        {tx.status === 'PENDING' ? (
                          <>
                            {' '}
                            <Chip tone="warn" icon="clock" small>
                              {he.cashflow.pending}
                            </Chip>
                          </>
                        ) : null}
                      </td>
                      <td>{tx.kind === 'CARD' ? <Mask>{cardMask}</Mask> : he.cashflow.filterChecking}</td>
                      <td>
                        <CategoryCell tx={tx} onPick={setCategory} />
                      </td>
                      <td className="n">
                        <Money agorot={tx.amountAgorot} decimals={2} plus />
                      </td>
                      <td className="n">
                        <VatCell tx={tx} />
                      </td>
                      <td>
                        <InvoiceCell expense={tx.expense} onMark={() => markInvoice(tx)} />
                      </td>
                      <td>
                        <MatchCell tx={tx} onMatch={openMatchDialog} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="list mob-only" style={{ flexDirection: 'column' }}>
              {groupByDay(sortRows(transactions.data.items, sort)).map(([day, rows]) => (
                <div key={day}>
                  <div className="day-h">
                    <Num>{fmtDate(day)}</Num>
                  </div>
                  {rows.map((tx) => (
                    <div key={tx.id}>
                      <TxListItem tx={tx} asOf={asOf} />
                      {(tx.amountAgorot ?? 0) > 0 && !tx.match ? (
                        <div style={{ padding: '0 4px 8px' }}>
                          <button type="button" className="btn sm primary" onClick={() => openMatchDialog(tx)}>
                            {he.cashflow.matchAction}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function sortRows(rows: TransactionRow[], sort: { col: SortCol; dir: 'asc' | 'desc' }): TransactionRow[] {
  const direction = sort.dir === 'asc' ? 1 : -1;
  const value = (tx: TransactionRow): string | number => {
    switch (sort.col) {
      case 'desc':
        return tx.merchant ?? tx.description;
      case 'account':
        return tx.kind;
      case 'cat':
        return tx.expense ? CATEGORY[tx.expense.category].label : SCOPE[tx.scope];
      case 'amount':
        return tx.amountAgorot ?? 0;
      case 'vat':
        return tx.expense?.vatAgorot ?? 0;
      default:
        return tx.effDate + tx.id;
    }
  };
  return rows.slice().sort((a, b) => {
    const va = value(a);
    const vb = value(b);
    if (va === vb) return 0;
    return (va > vb ? 1 : -1) * direction;
  });
}

function groupByDay(rows: TransactionRow[]): Array<[string, TransactionRow[]]> {
  const map = new Map<string, TransactionRow[]>();
  for (const row of rows) {
    const list = map.get(row.effDate) ?? [];
    list.push(row);
    map.set(row.effDate, list);
  }
  return [...map.entries()];
}

function MatchPicker({ candidates, asOf, onChange }: { candidates: MatchCandidate[]; asOf: string; onChange: (value: string | null) => void }) {
  const [value, setValue] = useState<string | null>(candidates[0]?.number ?? null);
  return (
    <div className="pick" role="radiogroup" aria-label={he.cashflow.matchTitle}>
      {candidates.map((candidate) => (
        <label key={candidate.number}>
          <input
            type="radio"
            name="match-candidate"
            checked={value === candidate.number}
            onChange={() => {
              setValue(candidate.number);
              onChange(candidate.number);
            }}
          />
          <span>
            <span className="ltr">{candidate.number}</span> · {candidate.customerName} · {fmtMoney(candidate.grossAgorot)}
          </span>
          <span className="pk-s">
            {candidate.overdueDays ? he.cashflow.matchOverdue(candidate.overdueDays) : fmtShortDate(candidate.dueDate, asOf)}
          </span>
        </label>
      ))}
      <label>
        <input
          type="radio"
          name="match-candidate"
          checked={value === null}
          onChange={() => {
            setValue(null);
            onChange(null);
          }}
        />
        <span>{he.cashflow.matchNone}</span>
      </label>
    </div>
  );
}
